import { useState } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, X } from "lucide-react";

// Searchable multi-select dropdown. Type to filter, click to toggle (stays open).
// `values` = array of selected values; `onChange(nextValues)`; `options` =
// [{ value, label, searchLabel? }]. Generic — use in any filter/report.
export const SearchableMultiSelect = ({
  values = [],
  onChange,
  options = [],
  placeholder = "Select...",
  emptyText = "No results.",
  className = "",
}) => {
  const [open, setOpen] = useState(false);

  const toggle = (v) => {
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  };

  const label =
    values.length === 0
      ? placeholder
      : values.length === 1
      ? options.find((o) => o.value === values[0])?.label || "1 selected"
      : `${values.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${values.length === 0 ? "text-muted-foreground" : ""} ${className}`}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." />
          {values.length > 0 && (
            <div className="flex items-center justify-between border-b px-2 py-1.5 text-xs text-slate-500">
              <span>{values.length} selected</span>
              <button type="button" className="inline-flex items-center hover:text-red-500" onClick={() => onChange([])}>
                <X className="mr-0.5 h-3 w-3" /> Clear
              </button>
            </div>
          )}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const checked = values.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.searchLabel || opt.label}
                    onSelect={() => toggle(opt.value)}
                  >
                    <Check className={`mr-2 h-4 w-4 ${checked ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{opt.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableMultiSelect;
